import produce from 'immer';
import ReactDOM from 'react-dom';
import createSagaMiddleware from 'redux-saga';
import { all, fork } from 'redux-saga/effects';
import { compose, createStore, applyMiddleware, bindActionCreators } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { reducer as oidcReducer } from 'redux-oidc';
import { createBrowserHistory } from 'history';
import { createReduxHistoryContext } from 'redux-first-history';
import { combineReducers } from 'redux-immer';
import * as actions from './actions';
import {
  app,
  statics,
  wizard,
  profile,
  addresses,
  documents,
  accounts,
  clients,
  modals,
  sagaStatus,
  wallets,
  mandates,
  preprocessingFundProfiles,
  peerProfiles,
  peerCategories,
  aums,
  fees,
  simulations,
  portfolios,
  modelPortfolios,
  notifications,
  crm,
  instruments,
  investmentObjectives,
  users,
  watchlists,
  otp,
  financialAdvisor,
} from './reducers';
import { sagas, sagasWithoutUser } from './sagas';

// eslint-disable-next-line no-underscore-dangle
const _compose =
  process.env.REACT_APP_HIDE_REDUX_DEVTOOLS === 'true' ? compose : composeWithDevTools;

const createStoreHistoryFactory = (defaultReducers = {}, defaultSagas = []) => (
  customReducers = {},
  customSagas = [],
  debugHistory
) => {
  const allSagas = [...defaultSagas, ...customSagas];

  function* rootSaga() {
    yield all(allSagas.map((s) => fork(s)));
  }

  const sagaMiddleware = createSagaMiddleware();

  const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
    history: createBrowserHistory(),
    savePreviousLocations: 10,
    batch: ReactDOM.unstable_batchedUpdates,
  });

  const store = createStore(
    combineReducers(produce, {
      router: routerReducer,
      ...defaultReducers,
      ...customReducers,
    }),
    _compose(applyMiddleware(sagaMiddleware), applyMiddleware(routerMiddleware))
  );

  const history = createReduxHistory(store);

  sagaMiddleware.run(rootSaga);

  // history debug
  if (debugHistory) {
    // eslint-disable-next-line
    history.listen((...args) => console.log('historyRedux', ...args));
  }

  store.actions = bindActionCreators(actions, store.dispatch);
  return {
    store,
    history,
  };
};

const allReducers = {
  oidc: oidcReducer,
  app,
  modals,
  profile,
  addresses,
  documents,
  statics,
  wizard,
  accounts,
  clients,
  wallets,
  mandates,
  preprocessingFundProfiles,
  peerProfiles,
  peerCategories,
  aums,
  fees,
  simulations,
  portfolios,
  modelPortfolios,
  notifications,
  sagaStatus,
  crm,
  instruments,
  investmentObjectives,
  users,
  watchlists,
  otp,
  financialAdvisor,
};

export const createStoreHistory = createStoreHistoryFactory(allReducers, sagas);
export const createStoreHistoryForStorybook = createStoreHistoryFactory(
  allReducers,
  sagasWithoutUser
);
export const createStoreHistorySSO = createStoreHistoryFactory({ app, modals });
